<template>
  <div :class="$style.wrapper">
    <div :class="$style.listContainer">
      <div ref="scrollableList" :class="$style.scrollableList">
        <ShopTab
          v-for="shop in shops"
          :key="shop.name"
          ref="shopTabs"
          :shop-name="shop.name"
          :shop-id="shop.id"
          :is-active="shop.id === activeShop?.id"
          :is-editable="canUserEditShops"
          :is-shop-default="shop.isDefault"
          @set-shop-as-default="setShopAsDefault(shop.id)"
          @edit-shop="handleEditShop(shop)"
          @click="selectShop(shop)"
        />
        <div v-if="shops.length > 0" ref="lastShopTab"></div>
      </div>
      <div :class="$style.addButton" @click="handleAddShop">+</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { Shop } from "../shop.types";
import ShopTab from "./ShopTab.vue";

const props = defineProps<{
  shops: Shop[];
  activeShop: Shop | null;
}>();

const route = useRoute();
const versionId = route.params.versionId as string;

const canUserEditShops = computed(() => versionId === "wip");
const emit = defineEmits(["add-shop", "select-shop", "update-shop", "edit-shop"]);
const scrollableList = ref<HTMLElement | null>(null);
const isAddingShop = ref(false);

const handleAddShop = () => {
  isAddingShop.value = true;
  emit("add-shop");
};

const handleEditShop = (shop: Shop) => {
  emit("edit-shop", shop);
};

const selectShop = (shop: Shop) => {
  emit("select-shop", shop);
};

watch(
  () => props.shops.length,
  (newLength, oldLength) => {
    if (newLength > oldLength && isAddingShop.value) {
      nextTick(() => {
        if (scrollableList.value) {
          scrollableList.value.scrollLeft = scrollableList.value.scrollWidth;
        }
        isAddingShop.value = false;
      });
    }
  }
);

const setShopAsDefault = (shopId: string) => {
  emit("update-shop", { shopId, isDefault: true });
};
</script>

<style module>
.wrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.listContainer {
  display: flex;
  overflow: hidden;
}

.scrollableList {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  flex-wrap: nowrap;
  flex-grow: 1;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollableList::-webkit-scrollbar {
  width: 0;
  background: transparent;
}

.addButton {
  background-color: var(--vd-color-blue-light-9);
  color: var(--vd-color-blue-light-3);
  border: var(--vd-color-blue-light-3) 1px solid;
  border-radius: 5px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  flex-shrink: 0;
  margin-left: 1rem;
}

.addButton:hover {
  background-color: var(--vd-color-white);
}
</style>
